<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="merchant-agreement-builder-page">
    <b-overlay
      :show="loading"
      rounded="sm"
      opacity="0.5"
      spinner-variant="primary"
    >
      <b-row class="match-height">
        <!-- ================= LEFT PANEL: Master Agreement Template Editor ================= -->
        <b-col
          lg="8"
          cols="12"
        >
          <b-card
            no-body
            class="shadow-sm border-0 h-100 d-flex flex-column"
          >
            <!-- Card Header -->
            <div class="d-flex justify-content-between align-items-center p-2 border-bottom-light">
              <h4 class="font-weight-bolder m-0 text-dark">
                Master Agreement Template
              </h4>
              <b-button
                variant="outline-secondary"
                size="sm"
                class="d-flex align-items-center btn-preview"
                @click="openPreviewModal"
              >
                <feather-icon
                  icon="EyeIcon"
                  class="mr-50"
                  size="14"
                />
                Preview Agreement
              </b-button>
            </div>

            <!-- Editor Body -->
            <div class="card-body p-2 flex-grow-1 d-flex flex-column">
              <div class="editor-wrapper flex-grow-1 d-flex flex-column">
                <quill-editor
                  ref="quillEditor"
                  v-model="agreementTemplate"
                  class="agreement-quill-editor flex-grow-1"
                  :options="editorOptions"
                />
              </div>
            </div>

            <!-- Card Footer -->
            <div class="p-2 border-top-light d-flex justify-content-end">
              <b-button
                v-can="'edit-merchant-agreement-setting'"
                variant="primary"
                class="px-3"
                :disabled="saving"
                @click="saveTemplate"
              >
                <b-spinner
                  v-if="saving"
                  small
                  class="mr-50"
                />
                Submit
              </b-button>
            </div>
          </b-card>
        </b-col>

        <!-- ================= RIGHT PANEL: Merchant Data Tags ================= -->
        <b-col
          lg="4"
          cols="12"
        >
          <b-card
            no-body
            class="shadow-sm border-0 h-100 d-flex flex-column"
          >
            <!-- Tags Header -->
            <div class="p-2 border-bottom-light flex-shrink-0">
              <div class="d-flex align-items-center">
                <h5 class="font-weight-bolder m-0 text-dark mr-50">
                  Merchant Data Tags
                </h5>
                <feather-icon
                  v-b-tooltip.hover="'Click a tag to insert it at the cursor position in the editor.'"
                  icon="HelpCircleIcon"
                  size="15"
                  class="text-muted cursor-pointer"
                />
              </div>
              <small class="text-muted d-block mt-25">
                Click a tag to insert it at the cursor.
              </small>

              <!-- Tag Search Input -->
              <div class="mt-1">
                <b-form-input
                  v-model="tagSearchQuery"
                  placeholder="Search tags"
                  class="tag-search-input"
                />
              </div>
            </div>

            <!-- Tags List Body -->
            <div class="card-body p-2 flex-grow-1 overflow-auto tag-list-container">
              <div
                v-if="filteredTags.length === 0"
                class="text-center text-muted py-4"
              >
                <feather-icon
                  icon="TagIcon"
                  size="24"
                  class="mb-50 text-secondary"
                />
                <p class="font-small-3 m-0">
                  No tags found
                </p>
              </div>

              <div
                v-for="tag in filteredTags"
                :key="tag.key"
                class="tag-item-card p-1 mb-1 border rounded d-flex justify-content-between align-items-center"
                @click="insertTag(tag.key)"
              >
                <div class="tag-meta text-truncate pr-1">
                  <div class="tag-label font-weight-bold font-small-3 text-dark text-truncate">
                    {{ tag.label }}
                  </div>
                  <div class="tag-code font-small-2 text-muted text-monospace">
                    {{ formatTag(tag.key) }}
                  </div>
                </div>
                <b-button
                  variant="flat-primary"
                  size="sm"
                  class="btn-icon p-25 rounded-circle add-tag-btn"
                  @click.stop="insertTag(tag.key)"
                >
                  <feather-icon
                    icon="PlusIcon"
                    size="16"
                  />
                </b-button>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </b-overlay>

    <!-- ================= AGREEMENT PREVIEW MODAL ================= -->
    <b-modal
      id="modal-agreement-preview"
      ref="previewModal"
      size="xl"
      hide-footer
      centered
      no-close-on-backdrop
      modal-class="agreement-preview-modal"
      title="AGREEMENT PREVIEW"
    >
      <div class="pb-2">
        <div class="d-flex justify-content-end mb-1">
          <div class="zoom-toolbar d-flex align-items-center border rounded px-1 py-25 bg-white shadow-sm">
            <b-button
              variant="flat-secondary"
              size="sm"
              class="px-50 py-25 mr-50 font-small-3 font-weight-bolder d-flex align-items-center text-dark"
              @click="zoomFit"
            >
              <feather-icon
                icon="MaximizeIcon"
                size="13"
                class="mr-25"
              />
              Fit
            </b-button>

            <span class="toolbar-divider mx-25 text-muted">|</span>

            <b-button
              variant="flat-secondary"
              size="sm"
              class="px-25 py-25 font-weight-bolder text-dark"
              :disabled="zoomLevel <= 30"
              @click="zoomOut"
            >
              <feather-icon
                icon="MinusIcon"
                size="14"
              />
            </b-button>

            <span class="zoom-percent font-small-3 font-weight-bolder px-50 text-dark">
              {{ zoomLevel }}%
            </span>

            <b-button
              v-b-tooltip.hover="'Zoom in'"
              variant="flat-secondary"
              size="sm"
              class="px-25 py-25 font-weight-bolder text-dark"
              :disabled="zoomLevel >= 150"
              @click="zoomIn"
            >
              <feather-icon
                icon="PlusIcon"
                size="14"
              />
            </b-button>
          </div>
        </div>

        <!-- A4 Document Canvas Preview -->
        <div class="preview-document-container py-3">
          <div
            class="preview-page-canvas bg-white shadow"
            :style="canvasZoomStyle"
          >
            <!-- Document Header -->
            <div class="document-header pb-2 mb-2 border-bottom d-flex justify-content-between align-items-start">
              <div class="company-details">
                <h5 class="company-title text-primary font-weight-bolder mb-25">
                  {{ courier.company_name }}
                </h5>
                <div class="font-small-2 text-muted line-height-2">
                  {{ courier.company_address }}<br>
                  {{ courier.company_country }}<br>
                  {{ courier.company_phone }}
                </div>
              </div>

              <div class="document-meta text-right font-small-2 line-height-2">
                <div><strong>Merchant ID:</strong> {{ previewMeta.merchantId }}</div>
                <div><strong>Document:</strong> {{ previewMeta.documentName }}</div>
                <div><strong>Date:</strong> {{ previewMeta.date }}</div>
              </div>
            </div>

            <!-- Document Content with Live Sample Tag Resolution -->
            <div
              class="document-body"
              v-html="renderedAgreementHtml"
            />

            <!-- Document Footer -->
            <div class="document-footer pt-2 mt-4 border-top font-small-1 text-muted d-flex justify-content-between">
              <span>
                Generated by {{ courier.company_name }} Merchant Agreement Builder — {{ previewMeta.generatedAt }} - Preview Document
              </span>
              <span>Page 1 of 1</span>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BFormInput,
  BModal,
  BOverlay,
  BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import { RepositoryFactory } from '@/repository/RepositoryFactory'

// Quill imports
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import { quillEditor } from 'vue-quill-editor'

const MerchantRegistrationBuilderRepository = RepositoryFactory.get('merchantRegistrationBuilder')
const MerchantAgreementRepository = RepositoryFactory.get('merchantAgreement')

export default {
  name: 'MerchantAgreementBuilder',
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BFormInput,
    BModal,
    BOverlay,
    BSpinner,
    quillEditor,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return {
      loading: false,
      saving: false,
      tagSearchQuery: '',
      availableTags: [],
      courier: {},

      // Agreement content (Starts empty initially)
      agreementTemplate: '',

      // Quill editor options
      editorOptions: {
        theme: 'snow',
        placeholder: 'Compose your Master Agreement Template here. Use the tags panel on the right to insert dynamic merchant data...',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ header: [1, 2, 3, false] }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ align: [] }],
            ['clean'],
          ],
        },
      },

      // Zoom scale percentage
      zoomLevel: 100,

      // Sample dataset used to replace merge tags in the preview modal
      sampleMerchantData: {
        business_name: 'Sample Merchant (Pvt) Ltd',
        first_name: 'Sample',
        last_name: 'Owner',
        address_line_1: 'No. 25, Galle Road',
        address_line_2: 'Suite 400',
        address_line_3: 'Kollupitiya',
        postal_code: '00300',
        postal_zip_code: '00300',
        country_id: 'Sri Lanka',
        state_id: 'Western Province',
        city_id: 'Colombo',
        contact_no: '077 123 4567',
        email: 'merchant@example.com',
        user_email: 'merchant@example.com',
        user_contact_no: '077 123 4567',
        account_name: 'Sample Merchant (Pvt) Ltd',
        account_no: '1002 3456 7890',
        bank_name: 'Sample Bank',
        branch_name: 'Colombo Main Branch',
        branch_code: '001',
        description: 'Retail & eCommerce Logistics partner.',
        remarks: 'Standard merchant account.',
        merchant_type: 'Credit',
      },
    }
  },
  computed: {
    // Filter tags by search input
    filteredTags() {
      const q = this.tagSearchQuery.trim().toLowerCase()
      if (!q) {
        return this.availableTags
      }
      return this.availableTags.filter(tag => {
        const { label, key } = tag
        const matchLabel = label.toLowerCase().includes(q)
        const matchKey = key.toLowerCase().includes(q)
        return matchLabel || matchKey
      })
    },

    // A4 Paper Zoom Transform
    canvasZoomStyle() {
      const scale = this.zoomLevel / 100
      return {
        transform: `scale(${scale})`,
        transformOrigin: 'top center',
      }
    },

    // Preview Meta Dates
    previewMeta() {
      const today = new Date().toISOString().split('T')[0]
      const nowTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      return {
        merchantId: 'MC-XXXX',
        documentName: 'MERCHANT-AGREEMENT',
        date: today,
        generatedAt: `${today} ${nowTime}`,
      }
    },

    // Rendered HTML with sample values replacing {{tag}}
    renderedAgreementHtml() {
      if (!this.agreementTemplate || !this.agreementTemplate.trim()) {
        return `
          <div class="text-center text-muted py-5">
            <p class="font-medium-1 mb-50">No agreement content written yet.</p>
            <small>Write your agreement in the editor and insert dynamic tags to preview it here.</small>
          </div>
        `
      }

      return this.agreementTemplate.replace(/{{\s*([a-zA-Z0-9_]+)\s*}}/g, (match, key) => {
        const sampleValue = this.sampleMerchantData[key]
        if (sampleValue !== undefined && sampleValue !== null) {
          return `<strong>${sampleValue}</strong>`
        }
        const formattedKey = key.replace(/_/g, ' ')
        return `<strong>[${formattedKey}]</strong>`
      })
    },
  },
  async created() {
    this.courier = JSON.parse(localStorage.getItem('businessSettings'))
    await this.fetchDataTags()
    await this.fetchTemplate()
  },
  methods: {
    formatTag(key) {
      return `{{${key}}}`
    },

    // Fetch dynamic tags from Form Builder configuration
    async fetchDataTags() {
      try {
        const response = await MerchantRegistrationBuilderRepository.getConfiguration()
        const { data: responseBody } = response
        const configData = responseBody.data ? responseBody.data : responseBody
        const { fields = [] } = configData

        // Only include fields that have is_tag enabled
        const taggedFields = fields.filter(f => Boolean(f.is_tag))

        if (taggedFields.length > 0) {
          this.availableTags = taggedFields.map(f => {
            const { key, label } = f
            return { key, label }
          })
        } else {
          this.availableTags = this.getDefaultTags()
        }
      } catch (err) {
        this.availableTags = this.getDefaultTags()
      }
    },

    getDefaultTags() {
      return [
        { key: 'business_name', label: 'Business Name' },
        { key: 'address_line_1', label: 'Address Line 1' },
        { key: 'address_line_2', label: 'Address Line 2' },
        { key: 'address_line_3', label: 'Address Line 3' },
        { key: 'postal_code', label: 'Postal / Zip Code' },
        { key: 'country', label: 'Country' },
        { key: 'state', label: 'State' },
        { key: 'city', label: 'City' },
        { key: 'contact_no', label: 'Contact No.' },
        { key: 'email', label: 'Email' },
        { key: 'first_name', label: 'First Name' },
        { key: 'last_name', label: 'Last Name' },
        { key: 'account_name', label: 'Account Name' },
        { key: 'account_no', label: 'Account No.' },
        { key: 'bank_name', label: 'Bank Name' },
        { key: 'branch_name', label: 'Branch Name' },
      ]
    },

    // Fetch saved template (Starts empty if none saved)
    async fetchTemplate() {
      this.loading = true
      try {
        if (MerchantAgreementRepository && MerchantAgreementRepository.getAgreementTemplate) {
          const res = await MerchantAgreementRepository.getAgreementTemplate()
          const { data } = res
          const payload = data.data ? data.data : data
          this.agreementTemplate = payload.template || ''
        } else {
          this.agreementTemplate = ''
        }
      } catch (err) {
        this.agreementTemplate = ''
      } finally {
        this.loading = false
      }
    },

    // Insert tag at current Quill cursor position
    insertTag(tagKey) {
      if (!this.$refs.quillEditor) return
      const { quill } = this.$refs.quillEditor

      quill.focus()
      const selection = quill.getSelection()
      const index = selection ? selection.index : quill.getLength() - 1
      const tagText = `{{${tagKey}}}`

      quill.insertText(index, tagText, 'user')
      quill.setSelection(index + tagText.length, 0, 'silent')
    },

    // Save Template
    async saveTemplate() {
      this.saving = true
      try {
        const payload = {
          template: this.agreementTemplate,
        }

        const res = await MerchantAgreementRepository.saveAgreementTemplate(payload)
        const { data } = res
        this.showSuccessMessage(data.message || 'Agreement template saved successfully.')
      } catch (err) {
        this.convertAndNotifyError(err)
      } finally {
        this.saving = false
      }
    },

    // Preview Modal Zoom Handlers
    openPreviewModal() {
      this.zoomFit()
      this.$refs.previewModal.show()
    },

    zoomIn() {
      if (this.zoomLevel < 150) {
        this.zoomLevel += 10
      }
    },

    zoomOut() {
      if (this.zoomLevel > 30) {
        this.zoomLevel -= 10
      }
    },

    zoomFit() {
      this.zoomLevel = 100
    },
  },
}
</script>

<style lang="scss" scoped>
.merchant-agreement-builder-page {
  .border-bottom-light {
    border-bottom: 1px solid #ebe9f1;
  }
  .border-top-light {
    border-top: 1px solid #ebe9f1;
  }

  .btn-preview {
    font-size: 0.85rem;
    font-weight: 500;
    color: #5e5873;
    border-color: #d8d6de;
    &:hover {
      background-color: #f8f8f8;
      color: #333;
    }
  }

  /* Quill Editor Styling */
  .editor-wrapper {
    min-height: 480px;
    display: flex;
    flex-direction: column;
    margin-bottom: 1.5rem;

    ::v-deep .ql-toolbar.ql-snow {
      border: 1px solid #ebe9f1;
      border-radius: 6px 6px 0 0;
      background: #fafafc;
    }

    ::v-deep .ql-container.ql-snow {
      border: 1px solid #ebe9f1;
      border-top: none;
      border-radius: 0 0 6px 6px;
      font-size: 0.95rem;
      min-height: 420px;
      background: #ffffff;
      flex-grow: 1;
    }

    ::v-deep .ql-editor {
      min-height: 420px;
      line-height: 1.8;
      color: #333;
    }
  }

  /* Merchant Data Tags List Styling */
  .tag-search-input {
    font-size: 0.88rem;
    border-color: #d8d6de;
  }

  .tag-list-container {
    /* 👈 Removes max-height and forces container to fill 100% of remaining card height */
    flex: 1 1 0;
    height: 0;
    min-height: 0;
    overflow-y: auto;

    /* Sleek custom scrollbar matching Screenshot 1 */
    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-track {
      background: #f8f8f8;
      border-radius: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background: #d8d6de;
      border-radius: 4px;
      &:hover {
        background: #b9b9c3;
      }
    }

    .tag-item-card {
      background: #ffffff;
      border-color: #ebe9f1 !important;
      transition: all 0.2s ease-in-out;
      cursor: pointer;

      &:hover {
        border-color: #7367f0 !important;
        background: #fbfbfe;

        .add-tag-btn {
          color: #7367f0;
          background-color: rgba(115, 103, 240, 0.12);
        }
      }

      .tag-code {
        font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
        color: #82868b;
      }

      .add-tag-btn {
        color: #b9b9c3;
        transition: color 0.2s;
      }
    }
  }
}

/* Modal A4 Preview Canvas Styling */
::v-deep .agreement-preview-modal,
.agreement-preview-modal {
  font-family: Helvetica, Arial, sans-serif;

  .modal-content {
    font-family: Helvetica, Arial, sans-serif;
  }

  .zoom-toolbar {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    .toolbar-divider {
      color: #ebe9f1;
      font-size: 0.85rem;
    }

    .zoom-percent {
      min-width: 45px;
      text-align: center;
    }
  }

  /* Preview Container */
  .preview-document-container {
    background: #eef0f4;
    border-radius: 6px;
    overflow-y: auto;
    overflow-x: auto;
    display: block; /* 👈 Using block prevents flexbox from shrinking the canvas */
    text-align: center;
    min-height: 600px;
    max-height: 80vh;
    padding: 2rem 1rem 4rem 1rem;
  }

  /* A4 Paper Canvas */
  .preview-page-canvas {
    width: 794px; /* Standard A4 width in px at 96 DPI */
    min-height: 1123px; /* Standard A4 height */
    height: auto !important; /* 👈 Forces the canvas to expand with long content */
    display: flex; /* 👈 Flex column structure */
    flex-direction: column;
    justify-content: space-between;
    flex-shrink: 0;
    margin: 0 auto;
    text-align: left; /* Resets text alignment */
    background-color: #ffffff !important;
    padding: 60px 60px 40px 60px;
    border-radius: 4px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
    transition: transform 0.2s ease-out;

    .company-title {
      font-size: 1.15rem;
      letter-spacing: 0.5px;
    }

    /* Document Body */
    .document-body {
      flex: 1 0 auto; /* 👈 Expands naturally without being cut off */
      font-size: 0.95rem;

      h3, h4, h5 {
        font-weight: 700;
        line-height: 1.2 !important;
        margin-bottom: 5px;
      }

      p {
        line-height: 1.2 !important;
        margin-bottom: 5px !important;
      }

      ul, ol {
        line-height: 1.2 !important;
        margin-bottom: 5px;
        padding-left: 1.5rem;
      }
    }

    /* Document Footer */
    .document-footer {
      flex-shrink: 0;
      margin-top: auto; /* 👈 Anchors footer to bottom */
    }
  }
}
</style>
